import React, { useEffect, useState } from "react";
import axios from "axios";
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
} from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { jwtDecode } from "jwt-decode";

import Mainscreen from "./screens/LandingPage";
import { initializeRUM } from "./awsRUM";
import authConfig from "./authConfig";
import userAccessService from "./services/userAccessService";
import SessionWarningModal from "./components/Modals/SessionWarningModal";
import "./App.css";

// Route-level code splitting.
//
// These pages were statically imported, which meant a single main bundle
// carried every screen in the app plus their heavy dependencies: Chart.js for
// the three dashboards, html2canvas and jsPDF for dashboard export, JSZip for
// series export, all of lodash, and react-joyride for each guided tour. That
// came to ~3.4 MB of minified JavaScript that every user downloaded, parsed and
// executed before the app became interactive, whichever page they were opening.
//
// Splitting per route means a planner opening /vff no longer pays for the
// dashboard's charting stack, and vice versa.
//
// LandingPage is deliberately left as a static import: it is the "/" route and
// the most common entry point, so lazy-loading it would add a round trip to the
// critical path for no real benefit (it is a light screen).
const DashboardPage = React.lazy(() => import("./screens/DashboardPage"));
const PTSachNrPage = React.lazy(() => import("./screens/PTSachNrPage"));
const VFFPage = React.lazy(() => import("./screens/VFFPage"));
const SerieTrackingPage = React.lazy(() => import("./Tabs/SerieTrackingPage"));
const VehiclePlanningApp = React.lazy(() => import("./vp/App"));
const ShopfloorProductionApp = React.lazy(
	() => import("./shopfloor/ShopfloorProductionApp"),
);

// Shown while a route chunk is being fetched.
const RouteLoadingFallback = () => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			height: "100vh",
			width: "100%",
		}}
	>
		<CircularProgress sx={{ color: "#cc0000" }} size={48} />
	</Box>
);

// How many seconds before expiry to show the warning modal
const SESSION_WARNING_BEFORE_EXPIRY_S = 600; // 10 minutes

const ENABLE_VFF_PAGE = true;
const ENABLE_SERIES_PAGE = true;

// Signin page component to handle redirect without causing render loopss
const SigninPage = ({ redirectToLogin }) => {
	useEffect(() => {
		const timer = setTimeout(() => {
			redirectToLogin();
		}, 100); // Small delay to avoid immediate redirect loop

		return () => clearTimeout(timer);
	}, [redirectToLogin]);

	return (
		<Box
			sx={{
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				height: "100vh",
				flexDirection: "column",
			}}
		>
			<Typography variant="h6" sx={{ mb: 2 }}>
				Redirecting to MAN Login...
			</Typography>
			<Typography variant="body2" color="text.secondary">
				Please wait while we redirect you to the authentication portal.
			</Typography>
		</Box>
	);
};

function App() {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [sessionWarning, setSessionWarning] = useState({
		open: false,
		secondsLeft: SESSION_WARNING_BEFORE_EXPIRY_S,
	});
	// ── Dev helper: open the session warning modal on demands ──────────────────
	// In the browser console run:  window.__testSessionWarning(120)
	// The argument is the secondsLeft value (default 120 = 2 minutes).
	useEffect(() => {
		window.__testSessionWarning = (secondsLeft = 120) => {
			setSessionWarning({ open: true, secondsLeft });
			console.info(
				`%c[SessionWarning] Modal opened with ${secondsLeft}s remaining`,
				"color: #e65100; font-weight: bold;",
			);
		};
		console.info(
			"%c[Dev] Session warning test helper ready → window.__testSessionWarning(seconds)",
			"color: #1e6b6b; font-size: 11px;",
		);
		return () => {
			delete window.__testSessionWarning;
		};
	}, []);

	// Add watermark to console (prevent duplicate logs in Strict Mode)
	useEffect(() => {
		if (!window.watermarkLogged) {
			console.log(
				"%c🚀 Developer: yasshsakhare",
				"color: #cc0000; font-size: 16px; font-weight: bold;",
			);
			console.log(
				"%c Created and Designed (2025): Team Data Science & yasshsakhare",
				"color: #4ecdc4; font-size: 14px;",
			);
			console.log("%c Watermark: © MAN", "color: #45b7d1; font-size: 12px;");
			window.watermarkLogged = true;
		}
	}, []);

	useEffect(() => {
		async function handleAuth() {
			try {
				const urlParams = new URLSearchParams(window.location.search);
				const code = urlParams.get("code");

				const expiryTime = localStorage.getItem("expiryTime");
				const refreshToken = localStorage.getItem("refreshToken");
				const currentTime = Math.floor(Date.now() / 1000);

				if (code && !localStorage.getItem("idToken")) {
					await exchangeCodeForToken(code);
					window.history.replaceState(
						{},
						document.title,
						window.location.pathname,
					);
				} else if (localStorage.getItem("idToken")) {
					if (expiryTime && currentTime > parseInt(expiryTime)) {
						if (refreshToken) {
							await refreshAccessToken();
						} else {
							setIsAuthenticated(false);
							setIsLoading(false);
							return;
						}
					} else {
						setIsAuthenticated(true);
					}
				} else {
					setIsAuthenticated(false);
				}
			} catch (error) {
				console.error("Auth error:", error);
				setIsAuthenticated(false);
			} finally {
				setIsLoading(false);
			}
		}

		handleAuth();

		if (window.location.hostname !== "localhost") {
			initializeRUM();
		}

		const tokenRefreshInterval = setInterval(() => {
			const expiryTime = localStorage.getItem("expiryTime");
			const refreshToken = localStorage.getItem("refreshToken");
			const currentTime = Math.floor(Date.now() / 1000);

			if (expiryTime) {
				const secondsLeft = parseInt(expiryTime) - currentTime;

				if (secondsLeft <= 0) {
					// Token has expired
					if (refreshToken) {
						refreshAccessToken();
					} else {
						setSessionWarning({ open: false, secondsLeft: 0 });
						signOutViaCognito();
					}
				} else if (secondsLeft <= SESSION_WARNING_BEFORE_EXPIRY_S) {
					// Within the warning window — show the modal (only open it once)
					setSessionWarning((prev) =>
						prev.open ? prev : { open: true, secondsLeft },
					);
				}
			}
		}, 30000);

		return () => clearInterval(tokenRefreshInterval);
	}, []);

	const redirectToLogin = () => {
		localStorage.clear();
		window.location.href = `https://${authConfig.cognitoDomain}/oauth2/authorize?client_id=${authConfig.clientId}&response_type=code&redirect_uri=${authConfig.redirectUri}&scope=openid+phone+email`;
	};

	const signOutViaCognito = () => {
		localStorage.clear();
		const logoutUri = encodeURIComponent(authConfig.redirectUri);
		window.location.href =
			`https://${authConfig.cognitoDomain}/logout` +
			`?client_id=${authConfig.clientId}` +
			`&logout_uri=${logoutUri}`;
	};

	const handleStayLoggedIn = async () => {
		setSessionWarning({
			open: false,
			secondsLeft: SESSION_WARNING_BEFORE_EXPIRY_S,
		});
		await refreshAccessToken();
	};

	const handleLogOutNow = () => {
		setSessionWarning({
			open: false,
			secondsLeft: SESSION_WARNING_BEFORE_EXPIRY_S,
		});
		signOutViaCognito();
	};

	const exchangeCodeForToken = async (code) => {
		try {
			const response = await axios.post(
				`https://${authConfig.cognitoDomain}/oauth2/token`,
				new URLSearchParams({
					grant_type: "authorization_code",
					client_id: authConfig.clientId,
					redirect_uri: authConfig.redirectUri,
					code,
				}),
				{ headers: { "Content-Type": "application/x-www-form-urlencoded" } },
			);
			storeTokens(response.data);
			setIsAuthenticated(true);
		} catch (error) {
			console.error("Token exchange error:", error);
			setIsAuthenticated(false);
		}
	};

	const refreshAccessToken = async () => {
		try {
			const refreshToken = localStorage.getItem("refreshToken");
			if (!refreshToken) {
				setIsAuthenticated(false);
				return;
			}

			const response = await axios.post(
				`https://${authConfig.cognitoDomain}/oauth2/token`,
				new URLSearchParams({
					grant_type: "refresh_token",
					client_id: authConfig.clientId,
					refresh_token: refreshToken,
				}),
				{ headers: { "Content-Type": "application/x-www-form-urlencoded" } },
			);
			storeTokens(response.data);
			setIsAuthenticated(true);
		} catch (error) {
			console.error("Token refresh error:", error);
			setIsAuthenticated(false);
		}
	};
	const storeTokens = (data) => {
		const expiryTime = Math.floor(Date.now() / 1000) + data.expires_in;
		localStorage.setItem("idToken", data.id_token);
		localStorage.setItem("accessToken", data.access_token);
		if (data.refresh_token) {
			localStorage.setItem("refreshToken", data.refresh_token);
		}
		localStorage.setItem("expiryTime", expiryTime.toString());
		try {
			const decodedToken = jwtDecode(data.id_token);
			localStorage.setItem(
				"username",
				decodedToken.email || decodedToken.phone_number || "User",
			);
		} catch (error) {
			console.error("Error decoding ID token:", error);
		}
		// Clear the user access cache so the next permission check uses the fresh token
		userAccessService.clearCache();
	};

	const username = localStorage.getItem("username") || "User";

	// Show loading while determining authentication state
	if (isLoading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
					flexDirection: "column",
				}}
			>
				<Typography variant="h6" sx={{ mb: 2 }}>
					Loading...
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Checking authentication status...
				</Typography>
			</Box>
		);
	}
	return (
		<>
			<SessionWarningModal
				open={sessionWarning.open}
				secondsLeft={sessionWarning.secondsLeft}
				onStayLoggedIn={handleStayLoggedIn}
				onLogOut={handleLogOutNow}
				onExpire={handleLogOutNow}
			/>
			<Router
				future={{
					v7_startTransition: true,
					v7_relativeSplatPath: true,
				}}
			>
				<React.Suspense fallback={<RouteLoadingFallback />}>
					<Routes>
						<Route
							path="/"
							element={
								isAuthenticated ? (
									<Mainscreen props={{ username }} />
								) : (
									<Navigate to="/signin" />
								)
							}
						/>{" "}
						<Route
							path="/supply-type-allocation"
							element={
								isAuthenticated ? (
									<PTSachNrPage
										user={{
											profile: {
												preferred_username: username?.includes("@")
													? username.split("@")[0]
													: username,
											},
										}}
									/>
								) : (
									<Navigate to="/signin" />
								)
							}
						/>
						{ENABLE_VFF_PAGE && (
							<Route
								path="/vff"
								element={
									isAuthenticated ? (
										<VFFPage
											user={{
												profile: {
													preferred_username: username?.includes("@")
														? username.split("@")[0]
														: username,
												},
											}}
										/>
									) : (
										<Navigate to="/signin" />
									)
								}
							/>
						)}{" "}
						{ENABLE_SERIES_PAGE && (
							<Route
								path="/series-tracking"
								element={
									isAuthenticated ? (
										<SerieTrackingPage
											user={{
												profile: {
													preferred_username: username?.includes("@")
														? username.split("@")[0]
														: username,
												},
											}}
										/>
									) : (
										<Navigate to="/signin" />
									)
								}
							/>
						)}{" "}
						<Route
							path="/dashboard"
							element={
								isAuthenticated ? (
									<DashboardPage
										user={{
											profile: {
												preferred_username: username?.includes("@")
													? username.split("@")[0]
													: username,
											},
										}}
									/>
								) : (
									<Navigate to="/signin" />
								)
							}
						/>
						<Route
							path="/signin"
							element={
								isAuthenticated ? (
									<Navigate to="/" />
								) : (
									<SigninPage redirectToLogin={redirectToLogin} />
								)
							}
						/>
						<Route
							path="/vehicle-planning"
							element={
								isAuthenticated ? (
									<VehiclePlanningApp />
								) : (
									<Navigate to="/signin" />
								)
							}
						/>
						<Route
							path="/shopfloor-production/*"
							element={
								isAuthenticated ? (
									<ShopfloorProductionApp />
								) : (
									<Navigate to="/signin" />
								)
							}
						/>
					</Routes>
				</React.Suspense>
			</Router>
		</>
	);
}

export default App;
